export enum StatusColor {
  GREEN = 'green',
  RED = 'red',
  ORANGE = 'orange',
  NO_COLOR = '',
}

export interface StockStatus {
  status: string
  statusColor: StatusColor
  statusReason: string
  notOnStockMessage?: string
}

export interface ProductServiceVideo {
  thumbnail: string
  mp4: string
  mp4Mobile: string
  webm: string
  webmMobile: string
  uploadDate: string
  duration: string
  videoDescription: string
}

export interface ProductServiceDocument {
  doctype: string
  link: string
}

export interface ProductServiceOfferPromotionProduct {
  code?: string
  description?: string
  headline?: string
  offerId?: string
  packshot?: string
  ribbonText?: string
  type: string
}

export interface ProductServiceOfferPromotion {
  offerPageNameAndTitle: string
  mappedPromotionProduct: ProductServiceOfferPromotionProduct
  mappedPromotion: string
}

export interface ProductServiceRedPoints {
  multiplierRP: number
  redPoints: number
  redPointsIsActive: boolean
  redPointsMultiplierIsValid: boolean
  endDateRP: string
}

export interface ProductServiceSubstances {
  activeSubstances: string
  additiveSubstances: string
}

export interface AlternateUrlsV1 {
  'de-DE'?: string
  'de-AT'?: string
  'fr-FR'?: string
  'it-IT'?: string
  'de-CH'?: string
  'fr-CH'?: string
}

export enum PimServiceAbdaSubstanceType {
  ACTIVE = 0,
  ADDITIVE = 1,
}

export interface PimIfapV1 {
  active?: boolean
  indication: string
  dosage: string
  modality: string
  sideeffects: string
  interaction: string
  contraindication: string
  pregnancy: string
  patientsdetails: string
  miscellaneous: string
}

export interface PimVideoV1 {
  thumbnail: string
  mp4: string
  mp4_mobile: string
  webm: string
  webm_mobile: string
  created: string
  duration_in_seconds: number
  description: string
}

export interface PimDocumentV1 {
  mimetype: string
  type: string
  url: string
}

export interface PimAbdaSubstanceV1 {
  agent_id: string
  type: PimServiceAbdaSubstanceType
  description: string
  quantity?: number
  unit?: string
}

export interface PimBrandV1 {
  code: string
  name: string
  path?: string
}

export interface PimPharmaFormV1 {
  code: string
  label: string
}

export interface PimCategoryV1 {
  code: string
  label: string
}

export interface PimICD10V1 {
  id: string
  description: string
}

export interface PimIndicationRangeV1 {
  code: string
  description: string
}

export interface PimManufacturerV1 {
  code: string
  name: string
}

export interface PimPackageSizeV1 {
  norm?: string
  unit: string
  quantity: string
  value: number
}

export interface PimSizeV1 {
  code: string
  label: string
}

export interface PimRatingV1 {
  count?: number
  average?: number
  path?: string
}

export interface PimFlagV1 {
  active: boolean
  label: string
}

export interface OfferDiscountV1 {
  percentage?: number
}

export interface OfferAdditionalInformationV1 {
  discount?: OfferDiscountV1
  netPrice?: number
  redPoints?: RedPointsV1
}

export interface OfferSellerV1 {
  displayName?: string
  sellerGrade?: number
  sellerRatingCount?: number
  sellerId?: string
}

export enum Unit {
  MILLILITERS = 'ml',
  LITER = 'l',
  GRAM = 'g',
  KILOGRAM = 'kg',
}

export interface OfferBasePriceV1 {
  amount: number
  currency: string
  unit: Unit
  value: number
}

export interface RedPointsV1 {
  amount: number
  multiplier: number
  endDate?: string
  startDate?: string
}

export interface OfferPriceV1 {
  additionalInformation?: OfferAdditionalInformationV1
  bonus?: number
  currency?: string
  price?: number
  shippingPrice?: number
  totalPrice?: number
  basePrice?: OfferBasePriceV1
}

export interface AnatomicalTherapeuticChemicalsV1 {
  code: string
  name: string
}

export enum VatCode {
  FREE_OF_TAX = 'XX_XX',
  GERMANY_REDUCED = 'DE_SR',
  GERMANY_RR = 'DE_RR',
  AUSTRIA_SR = 'AT_SR',
  AUSTRIA_TS = 'AT_TS',
  AUSTRIA_BI = 'AT_BI',
  FRANCE_SR = 'FR_SR',
  FRANCE_RR = 'FR_RR',
  FRANCE_UT = 'FR_UT',
  FRANCE_PR = 'FR_PR',
  ITALY_SR = 'IT_SR',
  ITALY_RR = 'IT_RR',
  ITALY_FB = 'IT_FB',
  SWITZERLAND_SR = 'CH_SR',
  SWITZERLAND_RR = 'CH_RR',
}

export interface PimVatV1 {
  code: VatCode
  rate: number
}

export enum PimRedirectType {
  PRODUCT = 'product',
  CATEGORY = 'category',
}

export interface PimRedirectV1 {
  code: string
  type: PimRedirectType
}

export enum OfferType {
  MIRAKL = 'MIRAKL',
  SAE = 'SAE',
}

export enum PrescriptionTypes {
  FREE_PRESCRIPTION = 'freePrescription',
  INSURANCE_PRESCRIPTION = 'insurancePrescription',
  NO_PRESCRIPTION = 'noPrescription',
  PRIVATE_PRESCRIPTION = 'privatePrescription',
}

export interface OfferV1 {
  offerId?: string
  channel?: string
  offerFlags?: Array<string>
  offerPrice?: OfferPriceV1
  strikeThroughOfferPrice?: OfferPriceV1
  offerType?: OfferType
  prescriptionTypes: PrescriptionTypes[]
  prescriptionPriceMap?: { [key: string]: OfferPriceV1 }
  seller?: OfferSellerV1
}

export interface PimPriceV1 {
  currency: string
  amount: number
  formatted: string
}

export interface PimPricesV1 {
  [key: string]: PimPriceV1
}

export interface PimProductFlagsV1 {
  cool: PimFlagV1
  prescription_medicine: PimFlagV1
  pharma_obl: PimFlagV1
  veterinary: PimFlagV1
  vegetarian: PimFlagV1
  vegan: PimFlagV1
  food: PimFlagV1
  homeopathic: PimFlagV1
  herbal: PimFlagV1
  diet: PimFlagV1
  medicament: PimFlagV1
  bio: PimFlagV1
  marketplace: PimFlagV1
  medical_device: PimFlagV1
}

export enum PimStockStatus {
  AVAILABLE = 'available',
  OUT_OF_SALE = 'out_of_sale',
  OUT_OF_STOCK = 'out_of_stock',
  NOT_IN_CATALOG = 'not_in_catalog',
  FREE = 'free',
  SERVICE = 'service',
  VARIANT = 'variant',
}

export enum PimProductType {
  ARTICLE = 'article',
  SERVICE = 'service',
  SET = 'set',
}

export enum PimLifestyleProduct {
  TRUE = 'true',
  FALSE = 'false',
  TRUE_WITH_EXCEPTIONS = 'true_with_exceptions',
}

export interface PimProductV1 {
  alternate_urls?: AlternateUrlsV1
  atc?: AnatomicalTherapeuticChemicalsV1
  book_type?: string
  brand?: PimBrandV1
  canonical_url: string
  categories?: PimCategoryV1[]
  category?: PimCategoryV1
  color_number?: string
  created: Date
  description_short?: string
  description_long?: string
  description_extra?: string
  display_code?: string
  documents?: PimDocumentV1[]
  ean?: string
  external_leaflet?: boolean
  external_lmiv?: boolean
  feeding_recommendation?: string
  flags?: PimProductFlagsV1
  follow_up_product_code?: string
  usage?: string
  guaranteed_analysis?: string
  ingredients?: string
  lmiv_claim?: string
  general_marketing_text?: string
  indication?: string
  nutritional_information?: string
  additives_kg?: string
  usp_key_facts?: string
  composition?: string
  storage_usage_info?: string
  food_ingredients?: string
  manufacturer_details?: string
  netfilling_quantity?: string
  author_portrait?: string
  product_details?: string
  recommended_intake?: string
  icd10?: PimICD10V1[]
  ifap?: PimIfapV1
  indication_ranges?: PimIndicationRangeV1[]
  images?: string[]
  isbn?: string
  keywords?: string[]
  name: string
  manufacturer?: PimManufacturerV1
  meta_title?: string
  meta_description?: string
  mirakl_product_id?: string
  ods_partners?: string[]
  offers?: OfferV1[]
  order_limit?: number
  offers_count?: number
  out_of_stock_message: string
  package_size?: PimPackageSizeV1
  path: string
  pharma_form?: PimPharmaFormV1
  prices?: PimPricesV1
  promotion?: unknown // check promotion type
  promotionProduct?: unknown // check promotionProduct type
  pzn?: string
  rating?: PimRatingV1
  redirect?: PimRedirectV1
  rejection_reason?: string
  size?: PimSizeV1
  stock_status: PimStockStatus
  substances?: PimAbdaSubstanceV1[]
  type: PimProductType
  topseller_count?: unknown
  updated: Date
  upid: string
  variant_code?: string
  vat?: PimVatV1
  variants?: unknown // check variants type
  videos?: PimVideoV1[]
  origin?: string
  lifestyle_product?: PimLifestyleProduct
}
